// @refresh reset
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteConfig } from './index';
import BasicLayout from '@/layouts/BasicLayout';
import {
  // Clapperboard,
  LayersIcon,
  SendIcon,
  UsersIcon,
  BarChart3,
  ChartColumnBig,
  ClipboardList,
} from 'lucide-react';
import { withSuspense } from './lazy';
import { DynamicRedirect } from './DynamicRedirect';
import { WorkbenchSkeleton } from '@/pages/Workbench/WorkbenchSkeleton';
import { TitledListPageSkeleton } from '@/components/PageSkeleton';
import { CreationManagementSkeleton } from '@/pages/DeliveryManagement/CreationManagement/CreationManagementSkeleton';
import { AccountManagementSkeleton } from '@/pages/AssetCenter/AccountManagement/AccountManagementSkeleton';
import { MaterialLibrarySkeleton } from '@/pages/AssetCenter/MaterialLibrary/MaterialLibrarySkeleton';
import { TextLibrarySkeleton } from '@/pages/AssetCenter/TextLibrary/TextLibrarySkeleton';
import { MediaResourceLibrarySkeleton } from '@/pages/AssetCenter/MediaResourceLibrary/MediaResourceLibrarySkeleton';
import { ResourceManagementSkeleton } from '@/pages/AssetCenter/ResourceManagement/ResourceManagementSkeleton';
import { LabelManagementSkeleton } from '@/pages/AssetCenter/LabelManagement/LabelManagementSkeleton';
import { FormPageSkeleton } from '@/pages/DeliveryManagement/CreationManagement/components/FormPageSkeleton';
import { PreviewPageSkeleton } from '@/pages/DeliveryManagement/CreationManagement/AddAdV2/AddADPreview/PreviewPageSkeleton';

// 业务页面
const Workbench = lazy(() => import('@/pages/Workbench'));
const AccountManagement = lazy(() => import('@/pages/AssetCenter/AccountManagement'));
const MaterialLibrary = lazy(() => import('@/pages/AssetCenter/MaterialLibrary'));
const TextLibrary = lazy(() => import('@/pages/AssetCenter/TextLibrary'));
const AppLibrary = lazy(() => import('@/pages/AssetCenter/AppLibrary'));
const MediaResourceLibrary = lazy(() => import('@/pages/AssetCenter/MediaResourceLibrary'));
const ResourceManagement = lazy(() => import('@/pages/AssetCenter/ResourceManagement'));
const LabelManagement = lazy(() => import('@/pages/AssetCenter/LabelManagement'));
const CreationManagement = lazy(() => import('@/pages/DeliveryManagement/CreationManagement'));
const StrategyTemplate = lazy(() => import('@/pages/DeliveryManagement/StrategyTemplate'));
const EditStrategyTemplate = lazy(
  () => import('@/pages/DeliveryManagement/StrategyTemplate/EditStrategyTemplate')
);
const AuthPage = lazy(() => import('@/pages/Auth'));
const ActivatePage = lazy(() => import('@/pages/Auth/Activate'));
const QRInvitationPage = lazy(() => import('@/pages/Auth/QRInvitation'));
// const Playlet = lazy(() => import('@/pages/Playlet/index'));
// const PlayletDetail = lazy(() => import('@/pages/Playlet/PlayletList'));
// const PlayletVideoDetails = lazy(() => import('@/pages/Playlet/VideoDetails'));
// const PlayletVideoEdit = lazy(() => import('@/pages/Playlet/VideoDetails/edit'));
// const PlayletUpload = lazy(() => import('@/pages/Playlet/UploadVideo'));
const AddADV2 = lazy(() => import('@/pages/DeliveryManagement/CreationManagement/AddAdV2'));
const AddADPreview = lazy(
  () => import('@/pages/DeliveryManagement/CreationManagement/AddAdV2/AddADPreview')
);
const DraftList = lazy(() => import('@/pages/DeliveryManagement/CreationManagement/DraftList'));
const EditDraft = lazy(
  () => import('@/pages/DeliveryManagement/CreationManagement/DraftList/EditDraft')
);
const DepartmentManagement = lazy(() => import('@/pages/TeamManagement/DepartmentManagement'));
const UserManagement = lazy(() => import('@/pages/TeamManagement/UserManagement'));
const CreateAdvertise = lazy(() => import('@/pages/TaskCenter/CreateAdvertise'));
const RetryTask = lazy(() => import('@/pages/TaskCenter/CreateAdvertise/RetryTask'));
const UploadTask = lazy(() => import('@/pages/TaskCenter/UploadTask'));
const MaterialDashboard = lazy(() => import('@/pages/ReportCenter/MaterialDashboard'));
const TeamDashboard = lazy(() => import('@/pages/ReportCenter/TeamDashboard'));
const MarketOverview = lazy(() => import('@/pages/ReportCenter/MarketOverview'));
const AdDashboard = lazy(() => import('@/pages/ReportCenter/AdDashboard'));

const NotFound = lazy(() => import('@/pages/NotFound'));

/**
 * 路由配置数组
 * 支持嵌套路由结构
 */
export const routes: RouteConfig[] = [
  {
    path: '/',
    element: <BasicLayout />,
    children: [
      {
        path: '',
        element: (
          <DynamicRedirect
            targetRoute={{ permCode: 'creative:workbench:menu', path: 'workbench' }}
          />
        ),
      },
      // 工作台
      {
        permCode: 'creative:workbench:menu',
        path: 'workbench',
        element: withSuspense(Workbench, { fallback: <WorkbenchSkeleton /> }),
        meta: {
          title: '工作台',
          icon: BarChart3,
        },
      },
      {
        permCode: 'creative:asset:menu',
        path: 'asset-center',
        meta: {
          title: '资产中心',
          icon: LayersIcon,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/asset-center/account-management" replace />,
          },
          {
            permCode: 'creative:account:sub_menu',
            path: 'account-management',
            element: withSuspense(AccountManagement, { fallback: <AccountManagementSkeleton /> }),
            meta: {
              title: '账户管理',
            },
          },
          {
            permCode: 'creative:material:sub_menu',
            path: 'material-library',
            element: withSuspense(MaterialLibrary, { fallback: <MaterialLibrarySkeleton /> }),
            meta: {
              title: 'Creative素材库',
            },
          },
          {
            permCode: 'creative:text:sub_menu',
            path: 'text-library',
            element: withSuspense(TextLibrary, { fallback: <TextLibrarySkeleton /> }),
            meta: {
              title: '文案库',
            },
          },
          {
            permCode: 'creative:app:sub_menu',
            path: 'app-library',
            element: withSuspense(AppLibrary, {
              fallback: <TitledListPageSkeleton columnCount={5} />,
            }),
            meta: {
              title: '应用库',
            },
          },
          {
            permCode: 'creative:media_material:sub_menu',
            path: 'media-resource-library',
            element: withSuspense(MediaResourceLibrary, {
              fallback: <MediaResourceLibrarySkeleton />,
            }),
            meta: {
              title: '媒体素材库',
            },
          },
          // 短剧
          // {
          //   permCode: 'creative:mini_series:sub_menu',
          //   path: 'playlet',
          //   meta: {
          //     title: '短剧库管理',
          //     icon: Clapperboard,
          //   },
          //   children: [
          //     {
          //       path: '',
          //       element: withSuspense(Playlet),
          //       meta: {
          //         title: '短剧库',
          //       },
          //     },
          //     {
          //       path: 'detail',
          //       element: withSuspense(PlayletDetail),
          //       meta: {
          //         title: '短剧列表',
          //       },
          //     },
          //     {
          //       path: 'video/:id',
          //       element: withSuspense(PlayletVideoDetails),
          //       meta: {
          //         title: '短剧详情',
          //       },
          //     },
          //     {
          //       path: 'video/:id/edit',
          //       element: withSuspense(PlayletVideoEdit),
          //       meta: {
          //         title: '编辑短剧',
          //       },
          //     },
          //     {
          //       path: 'upload',
          //       element: withSuspense(PlayletUpload),
          //       meta: {
          //         title: '上传短剧',
          //       },
          //     },
          //   ],
          // },
          {
            permCode: 'creative:tag:sub_menu',
            path: 'tag-management',
            element: withSuspense(LabelManagement, { fallback: <LabelManagementSkeleton /> }),
            meta: {
              title: '标签管理',
            },
          },
          {
            permCode: 'creative:asset_manage:sub_menu',
            path: 'resource-management',
            element: withSuspense(ResourceManagement, { fallback: <ResourceManagementSkeleton /> }),
            meta: {
              title: '资产管理',
            },
          },
        ],
      },
      // 投放管理
      {
        permCode: 'creative:delivery:menu',
        path: 'delivery-management',
        meta: {
          title: '投放管理',
          icon: SendIcon,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/delivery-management/creation-management" replace />,
          },
          {
            permCode: 'creative:create_edit:sub_menu',
            path: 'creation-management',
            meta: {
              title: '创编管理',
            },
            children: [
              {
                path: '',
                element: withSuspense(CreationManagement, {
                  fallback: <CreationManagementSkeleton />,
                }),
              },
              {
                permCode: 'creative:ad:add',
                path: 'add-ad',
                meta: {
                  title: '新建广告',
                },
                children: [
                  {
                    path: '',
                    element: withSuspense(AddADV2, {
                      fallback: <FormPageSkeleton withPreviewColumn />,
                    }),
                  },
                  {
                    permCode: 'creative:ad:add',
                    path: 'add-ad-preview',
                    meta: {
                      title: '广告预览',
                    },
                    element: withSuspense(AddADPreview, {
                      fallback: <PreviewPageSkeleton />,
                    }),
                  },
                ],
              },
              {
                permCode: 'creative:ad:draft',
                path: 'draft',
                meta: {
                  title: '草稿箱',
                },
                children: [
                  {
                    path: '',
                    element: withSuspense(DraftList, { fallback: <TitledListPageSkeleton /> }),
                  },
                  {
                    permCode: 'creative:ad:draft',
                    path: 'edit/:id',
                    meta: {
                      title: '编辑草稿',
                    },
                    element: withSuspense(EditDraft, { fallback: <FormPageSkeleton /> }),
                  },
                ],
              },
            ],
          },
          {
            permCode: 'creative:strategy_template:sub_menu',
            path: 'strategy-template',
            meta: {
              title: '策略模板',
            },
            children: [
              {
                path: '',
                element: withSuspense(StrategyTemplate, { fallback: <TitledListPageSkeleton /> }),
              },
              {
                permCode: 'creative:strategy_template:edit',
                path: 'edit/:id',
                meta: {
                  title: '编辑模板',
                },
                element: withSuspense(EditStrategyTemplate, { fallback: <FormPageSkeleton /> }),
              },
            ],
          },
          // {
          //   permCode: 'creative:mini_series_delivery:sub_menu',
          //   path: 'ms-delivery',
          //   element: ,//TODO 暂无对应页面
          //   meta: {
          //     title: '短剧投放',
          //   },
          // },
        ],
      },
      //数据报表
      // {
      //   permCode: 'creative:report:menu',
      //   path: 'data-report',
      //   element: ,//TODO 暂无对应页面
      //   meta: {
      //     title: '数据报表',
      //     icon: BarChart3,
      //   },
      // },
      //智能工具
      // {
      //   permCode: 'creative:tool:menu',
      //   path: 'intelligent-tools',
      //   element: ,//TODO 暂无对应页面
      //   meta: {
      //     title: '智能工具',
      //     icon: ToolboxIcon,
      //   },
      // },
      // 团队管理
      {
        permCode: 'creative:team:menu',
        path: 'team-management',
        meta: {
          title: '团队管理',
          icon: UsersIcon,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/team-management/department-management" replace />,
          },
          {
            permCode: 'creative:dept:sub_menu',
            path: 'department-management',
            element: withSuspense(DepartmentManagement, {
              fallback: <TitledListPageSkeleton headerActionCount={1} columnCount={10} />,
            }),
            meta: {
              title: '部门管理',
            },
          },
          {
            permCode: 'creative:user:sub_menu',
            path: 'user-management',
            element: withSuspense(UserManagement, {
              fallback: <TitledListPageSkeleton headerActionCount={1} columnCount={14} />,
            }),
            meta: {
              title: '用户管理',
            },
          },
        ],
      },
      // 开户管理
      {
        permCode: 'creative:task:menu',
        path: 'task-center',
        meta: {
          title: '任务中心',
          icon: ClipboardList,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/task-center/create-advertise" replace />,
          },
          {
            permCode: 'creative:create_ad:sub_menu',
            path: 'create-advertise',
            meta: {
              title: '创建广告',
            },
            children: [
              {
                path: '',
                element: withSuspense(CreateAdvertise, { fallback: <TitledListPageSkeleton /> }),
              },
              {
                permCode: 'creative:create_ad:retry',
                path: 'retry-task',
                element: withSuspense(RetryTask, {
                  fallback: <FormPageSkeleton withPreviewColumn actionCount={2} />,
                }),
                meta: {
                  title: '编辑重试',
                },
              },
            ],
          },
          {
            // TODO(perm): 暂复用 Creative 素材库页面的权限码，待权限侧下发专属 code
            // （如 creative:upload_task:sub_menu）后替换。
            permCode: 'creative:material:sub_menu',
            path: 'upload-task',
            element: withSuspense(UploadTask, { fallback: <TitledListPageSkeleton /> }),
            meta: {
              title: '上传素材',
            },
          },
        ],
      },
      {
        permCode: 'creative:report:menu',
        path: 'data-report',
        meta: {
          title: '报表中心',
          icon: ChartColumnBig,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/data-report/market-overview" replace />,
          },
          {
            permCode: 'creative:market_overview:sub_menu',
            path: 'market-overview',
            element: withSuspense(MarketOverview),
            meta: {
              title: '大盘概览',
            },
          },
          {
            permCode: 'creative:material_report',
            path: 'material-dashboard',
            element: withSuspense(MaterialDashboard),
            meta: {
              title: '素材看板',
            },
          },
          {
            permCode: 'creative:team_board',
            path: 'team-dashboard',
            element: withSuspense(TeamDashboard),
            meta: {
              title: '团队看板',
            },
          },
          {
            permCode: 'creative:ad_object_report:sub_menu',
            path: 'ad-dashboard',
            element: withSuspense(AdDashboard),
            meta: {
              title: '广告报表',
            },
          },
        ],
      },
      // 系统设置
      // {
      //   permCode: 'creative:system:menu',
      //   path: 'sys-setting',
      //   element: ,//TODO 暂无对应页面
      //   meta: {
      //     title: '系统设置',
      //     icon: SettingsIcon
      //   },
      // },
    ],
  },
  {
    path: '/login',
    element: withSuspense(AuthPage),
    meta: {
      title: '登录',
      requireAuth: false,
    },
  },
  {
    path: '/activate',
    element: withSuspense(ActivatePage),
    meta: {
      title: '账号激活',
      requireAuth: false,
    },
  },
  {
    path: '/qr-invitation',
    element: withSuspense(QRInvitationPage),
    meta: {
      title: '二维码邀请',
      requireAuth: false,
    },
  },
  {
    path: '/404',
    element: withSuspense(NotFound),
    meta: {
      title: '404',
      requireAuth: false,
    },
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
    meta: {
      title: '未找到',
      requireAuth: false,
    },
  },
];
